/**
 * ModerationQueue.tsx — the flagged-content queue shared by tool comments (FB-F17) and forum posts
 * (CH-F14): every OPEN flag, with a preview of what was flagged, how many distinct people flagged it,
 * and a decision — dismiss (content stays, reporter told it was reviewed) or remove, citing one of the
 * published rules (CH-F16). A single report never removes anything by itself; a person always decides.
 * Built on the Kombi design system (kombi/admin/chrome) and mounted as an in-place modal panel over AdminHome
 * (ModerationQueuePanel), rather than the old full-page navy screen.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a decision, calls reload() to refresh in place instead of a router revalidation. The still-registered
 * /moderation route renders through a thin ModerationQueue() wrapper that opens the same panel over a
 * blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiPost, type ModerationQueueItem, type PublishedRule } from './api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type ModerationQueueLoaderData =
  | { state: 'ok'; flags: ModerationQueueItem[]; rules: PublishedRule[] }
  | { state: 'signedOut' }
  | { state: 'forbidden' };

export async function moderationQueueLoader(): Promise<ModerationQueueLoaderData> {
  const flagsRes = await apiGet<{ flags: ModerationQueueItem[] }>('/api/moderation/queue');
  if (flagsRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (flagsRes.kind === 'error') {
    if (flagsRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/moderation/queue failed (${flagsRes.status})`);
  }

  const rulesRes = await apiGet<{ rules: PublishedRule[] }>('/api/moderation/rules');
  if (rulesRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (rulesRes.kind === 'error') {
    if (rulesRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/moderation/rules failed (${rulesRes.status})`);
  }

  return { state: 'ok', flags: flagsRes.body.flags, rules: rulesRes.body.rules };
}

function DecisionForm({
  flagId,
  rules,
  onDecided,
}: {
  flagId: string;
  rules: PublishedRule[];
  onDecided: () => void;
}) {
  const [reason, setReason] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: 'remove' | 'dismiss') {
    setError(null);
    if (reason.trim().length === 0) {
      setError('Every decision needs a reason — the person is told why.');
      return;
    }
    if (decision === 'remove' && !ruleId) {
      setError('Removing content must cite one of the published rules.');
      return;
    }
    setBusy(true);
    const res = await apiPost<void>(`/api/moderation/${flagId}/decision`, {
      decision,
      reason: reason.trim(),
      ...(decision === 'remove' ? { ruleId } : {}),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      onDecided();
      return;
    }
    if (res.kind === 'error' && res.status === 409) {
      setError('This flag has already been decided.');
      return;
    }
    // Deciding a flag needs a live Champion, SWAT, or PlatformOwner appointment. Name that instead of
    // the generic failure so a lost/insufficient role doesn't read as a random error.
    if (res.kind === 'unauthorized' || (res.kind === 'error' && res.status === 403)) {
      setError(
        'You need the Champion, SWAT, or platform-owner role to decide flags. Ask a platform owner to grant it (Admin → Appointments).',
      );
      return;
    }
    setError('Could not record the decision. Please try again.');
  }

  return (
    <div>
      <div className="kombi-dialog-stack">
        {/* htmlFor via the label's own `for`/id pair, kept rather than wrapping, because the reason
            and the rule are addressed by the ids the decide() payload references. */}
        <label className="kombi-field" htmlFor={`mod-reason-${flagId}`}>
          <span>Reason (shown to the person who posted it)</span>
          <textarea
            id={`mod-reason-${flagId}`}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
            value={reason}
          />
        </label>
        <label className="kombi-field" htmlFor={`mod-rule-${flagId}`}>
          <span>Rule cited (required to remove)</span>
          <select
            id={`mod-rule-${flagId}`}
            onChange={(e) => setRuleId(e.target.value)}
            value={ruleId}
          >
            <option value="">— select a published rule —</option>
            {rules.map((r) => (
              <option key={r.ruleId} value={r.ruleId}>
                {r.text}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <ErrorLine>{error}</ErrorLine>}

      <div className="kombi-admin-row-actions">
        {/* Remove is the destructive one, so it stays a quiet control in Kombi's error colour — the
            colour is the warning. Dismiss is the affirmative "keep it" action, so it is the primary. */}
        <QuietAction danger disabled={busy} onClick={() => void decide('remove')}>
          {busy ? 'Deciding…' : 'Remove'}
        </QuietAction>
        <PrimaryAction disabled={busy} onClick={() => void decide('dismiss')}>
          Dismiss (keep content)
        </PrimaryAction>
      </div>
    </div>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → moderationQueueLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the flagged-content list on success — each decision reload()s in place.
 */
export function ModerationQueuePanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(moderationQueueLoader);
  const [decided, setDecided] = useState<Set<string>>(new Set());

  // Reset the local "just decided" set whenever the loader re-runs with fresh data, so a
  // revalidated item that legitimately reappears (e.g. a second open flag on new content) is
  // never hidden by a stale local flag.
  useEffect(() => {
    setDecided(new Set());
  }, [data]);

  return (
    <KombiAdminOverlay title="Moderation queue" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading moderation queue…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the queue could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Moderation queue" />
      ) : data && data.state === 'ok' ? (
        <ModerationQueueBody
          data={data}
          reload={reload}
          decided={decided}
          setDecided={setDecided}
        />
      ) : null}
    </KombiAdminOverlay>
  );
}

/** The success body: intro, the no-rules warning, and the list of flags still awaiting a decision. */
function ModerationQueueBody({
  data,
  reload,
  decided,
  setDecided,
}: {
  data: { state: 'ok'; flags: ModerationQueueItem[]; rules: PublishedRule[] };
  reload: () => void;
  decided: Set<string>;
  setDecided: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const visible = data.flags.filter((f) => !decided.has(f.flagId));

  return (
    <div>
      <ProvisionalNote />
      <Notice>
        Content flagged by a colleague. It stays visible while you review it — nothing is removed on
        a single report.
      </Notice>

      {data.rules.length === 0 ? (
        <ErrorLine>
          No forum rules are published yet — a removal decision needs one to cite (CH-F16).
        </ErrorLine>
      ) : null}

      {visible.length === 0 ? (
        <EmptyState>No flagged content is waiting for a decision.</EmptyState>
      ) : (
        <ul className="kombi-dialog-stack">
          {visible.map((f) => (
            <li key={f.flagId} className="kombi-admin-card">
              <div className="kombi-admin-value">
                {f.targetKind === 'comment' ? 'Tool comment' : 'Forum post'} · flagged by{' '}
                {f.reporterName ?? '[former colleague]'}
              </div>
              <div className="kombi-admin-value kombi-admin-value--muted">
                {shortWhen(f.createdAt)} · {f.reportCount}{' '}
                {f.reportCount === 1 ? 'person has' : 'people have'} flagged this
              </div>
              {f.preview ? (
                <div className="kombi-admin-value">
                  <strong>{f.preview.authorName ?? '[former colleague]'}:</strong> {f.preview.body}
                </div>
              ) : (
                <p className="kombi-admin-empty">
                  This content has already been removed by an earlier decision on the same target.
                </p>
              )}
              <DecisionForm
                flagId={f.flagId}
                rules={data.rules}
                onDecided={() => {
                  setDecided((prev) => new Set(prev).add(f.flagId));
                  reload();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Thin route wrapper for the still-registered /moderation deep link. Opens the panel always-open over a
 * blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function ModerationQueue() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <ModerationQueuePanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
